import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Form, Row, Col, Badge, Modal, Dropdown } from 'react-bootstrap';
import { PlusIcon, EditIcon, TrashIcon } from '../common/Icons';
import { quoteService } from '../../services/api';
import { Quote, PaginatedResponse } from '../../types';
import { toast } from 'react-toastify';
import QuoteForm from './QuoteForm';
import QuoteDetails from './QuoteDetails';
import StatusBadge from '../common/StatusBadge';

const QuoteList: React.FC = () => {
  const [quotes, setQuotes] = useState<PaginatedResponse<Quote> | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<Quote | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [quoteToUpdateStatus, setQuoteToUpdateStatus] = useState<Quote | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;

      const response = await quoteService.getAll(params);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter]);

  const handleDelete = async () => {
    if (!quoteToDelete) return;

    try {
      await quoteService.delete(quoteToDelete.id);
      toast.success('Devis supprimé avec succès');
      fetchQuotes();
      setShowDeleteModal(false);
      setQuoteToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleConvertToInvoice = async (quote: Quote) => {
    if (quote.status === 'converted') {
      toast.warning('Ce devis a déjà été converti en facture');
      return;
    }

    try {
      const response = await quoteService.convertToInvoice(quote.id);
      toast.success(response.data.message || 'Devis converti en facture avec succès');
      fetchQuotes();
    } catch (error: any) {
      console.error('Erreur conversion:', error);
      const message = error.response?.data?.error || error.message || 'Erreur lors de la conversion';
      toast.error(message);
    }
  };

  const handleUpdateStatus = async () => {
    if (!quoteToUpdateStatus || !newStatus) return;

    try {
      const response = await quoteService.updateStatus(quoteToUpdateStatus.id, newStatus);
      toast.success(response.data.message || 'Statut mis à jour avec succès');
      fetchQuotes();
      setShowStatusModal(false);
      setQuoteToUpdateStatus(null);
      setNewStatus('');
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erreur lors de la mise à jour du statut';
      toast.error(message);
    }
  };

  const openStatusModal = (quote: Quote) => {
    setQuoteToUpdateStatus(quote);
    setNewStatus(quote.status);
    setShowStatusModal(true);
  };

  const openDetailsModal = (quoteId: number) => {
    setSelectedQuoteId(quoteId);
    setShowDetailsModal(true);
  };

  const handleExportPdf = async (quote: Quote) => {
    try {
      const response = await quoteService.generatePdf(quote.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis-${quote.quote_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };



  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Gestion des devis</h1>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <PlusIcon className="me-2" />
          Nouveau devis
        </Button>
      </div>

      <Card>
        <Card.Header>
          <Row>
            <Col md={6}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="sent">Envoyé</option>
                <option value="accepted">Accepté</option>
                <option value="rejected">Refusé</option>
                <option value="converted">Converti</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Valide jusqu'au</th>
                  <th>Montant TTC</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotes?.data.map((quote) => (
                  <tr key={quote.id}>
                    <td>
                      <Button 
                        variant="link" 
                        className="p-0 text-decoration-none"
                        onClick={() => openDetailsModal(quote.id)}
                      >
                        {quote.quote_number}
                      </Button>
                    </td>
                    <td>{quote.client?.name}</td>
                    <td>{new Date(quote.date).toLocaleDateString('fr-FR')}</td>
                    <td>{new Date(quote.valid_until).toLocaleDateString('fr-FR')}</td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(quote.total_ttc)}
                    </td>
                    <td><StatusBadge status={quote.status} type="quote" /></td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-primary" size="sm">
                          Actions
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => openDetailsModal(quote.id)}>
                            Voir les détails
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => openStatusModal(quote)}>
                            Modifier le statut
                          </Dropdown.Item>
                          {(quote.status === 'accepted' || quote.status === 'sent') && (
                            <Dropdown.Item onClick={() => handleConvertToInvoice(quote)}>
                              Convertir en facture
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item onClick={() => handleExportPdf(quote)}>
                            Télécharger PDF
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item 
                            className="text-danger"
                            onClick={() => {
                              setQuoteToDelete(quote);
                              setShowDeleteModal(true);
                            }}
                          >
                            Supprimer
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le devis "{quoteToDelete?.quote_number}" ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier le statut du devis</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Nouveau statut pour le devis "{quoteToUpdateStatus?.quote_number}"</Form.Label>
            <Form.Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <option value="draft">Brouillon</option>
              <option value="sent">Envoyé</option>
              <option value="accepted">Accepté</option>
              <option value="rejected">Refusé</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleUpdateStatus}>
            Mettre à jour
          </Button>
        </Modal.Footer>
      </Modal>

      <QuoteDetails
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        quoteId={selectedQuoteId}
        onUpdate={fetchQuotes}
      />

      <QuoteForm
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={fetchQuotes}
      />
    </div>
  );
};

export default QuoteList;